import React, { useState } from 'react';
import { EXERCISE_ID_TO_NODE } from '../../utils/lessonAiContentBlocks';
import { syntheticBlockForExId } from '../../utils/lessonAiContentBlocks';

// ─── Constants ────────────────────────────────────────────────────────────────

const EXERCISE_TYPES = [
  { id: 'fillinthegaps',      label: 'Fill in the Gaps',  icon: '✏️' },
  { id: 'truefalse',          label: 'True / False',       icon: '✅' },
  { id: 'worddefinition',     label: 'Word Definition',    icon: '📖' },
  { id: 'wordtranslation',    label: 'Word Translation',   icon: '🔤' },
  { id: 'matchinghalves',     label: 'Matching',           icon: '🔗' },
  { id: 'abcd',               label: 'Multiple Choice',    icon: '🔵' },
  { id: 'errorcorrection',    label: 'Error Correction',   icon: '🔍' },
  { id: 'warmupdiscussion',   label: 'Discussion',         icon: '💬' },
  { id: 'leadinactivities',   label: 'Lead-in',            icon: '🔥' },
  { id: 'createdialogue',     label: 'Dialogue',           icon: '🗣️' },
  { id: 'essentialvocabulary',label: 'Essential Vocab',    icon: '📚' },
];

const NODE_TO_EX_ID = Object.fromEntries(
  Object.entries(EXERCISE_ID_TO_NODE).map(([k, v]) => [v, k])
);

const SECTION_META = {
  leadin:     { icon: '🔥', label: 'Warm-up',    accent: '#7c3aed', bg: '#f5f3ff' },
  reading:    { icon: '📖', label: 'Reading',    accent: '#1d4ed8', bg: '#eff6ff' },
  vocabulary: { icon: '📚', label: 'Vocabulary', accent: '#065f46', bg: '#f0fdf4' },
  grammar:    { icon: '✍️',  label: 'Grammar',    accent: '#92400e', bg: '#fffbeb' },
  speaking:   { icon: '💬', label: 'Speaking',   accent: '#0e7490', bg: '#ecfeff' },
  listening:  { icon: '🎧', label: 'Listening',  accent: '#065f46', bg: '#f0fdf4' },
  homework:   { icon: '📝', label: 'Homework',   accent: '#374151', bg: '#f9fafb' },
};

const clr = {
  gray50:  '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  purple:  '#7c3aed',
  purpleSoft: '#ede9fe',
  purpleText: '#5b21b6',
  red: '#dc2626',
  redSoft: '#fef2f2',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function inferSectionType(sec) {
  const t = String(sec.title || '').toLowerCase();
  const bt = String(sec.blockType || '').toLowerCase();
  if (bt === 'homework' || t === 'homework' || t === 'writing') return 'homework';
  if (bt === 'main' || t.includes('warm') || t.includes('lead')) return 'leadin';
  if (t === 'vocabulary' || t.includes('vocab') || t.includes('lexis')) return 'vocabulary';
  if (t === 'grammar' || t.includes('grammar')) return 'grammar';
  if (t === 'speaking' || t === 'discussion' || t.includes('speak')) return 'speaking';
  if (t.includes('listen')) return 'listening';
  return 'reading';
}

function nodeToExId(nodeType) {
  return NODE_TO_EX_ID[nodeType] || 'fillinthegaps';
}

function exLabel(exId) {
  return EXERCISE_TYPES.find(t => t.id === exId) || EXERCISE_TYPES[0];
}

// ─── ExerciseRow ──────────────────────────────────────────────────────────────

function ExerciseRow({ exId, onChange, onRemove }) {
  const info = exLabel(exId);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 0,
        border: `1px solid ${clr.gray200}`, borderRadius: 7, overflow: 'hidden',
        background: '#fff',
      }}>
        <span style={{ padding: '0 8px', fontSize: 14, lineHeight: 1, flexShrink: 0 }}>{info.icon}</span>
        <select
          value={exId}
          onChange={e => onChange(e.target.value)}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 12, fontWeight: 500, color: clr.gray700,
            padding: '6px 4px 6px 0', cursor: 'pointer',
            appearance: 'none', WebkitAppearance: 'none',
          }}
        >
          {EXERCISE_TYPES.map(t => (
            <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
          ))}
        </select>
        <span style={{ paddingRight: 8, fontSize: 10, color: clr.gray400, pointerEvents: 'none' }}>▾</span>
      </div>
      <button
        onClick={onRemove}
        title="Remove exercise"
        style={{
          width: 26, height: 26, borderRadius: 6, border: `1px solid ${clr.gray200}`,
          background: '#fff', cursor: 'pointer', color: clr.gray400,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, flexShrink: 0, transition: 'all 120ms',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = clr.red; e.currentTarget.style.color = clr.red; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = clr.gray200; e.currentTarget.style.color = clr.gray400; }}
      >
        ✕
      </button>
    </div>
  );
}

// ─── SectionCard ──────────────────────────────────────────────────────────────

function SectionCard({ section, cefrLevel, lessonTitle, onUpdate, onDelete, onAiEdit, applying }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState('');

  const type = inferSectionType(section);
  const meta = SECTION_META[type] || SECTION_META.reading;
  const blocks = Array.isArray(section.contentBlocks) ? section.contentBlocks : [];

  const updateBlocks = (newBlocks) => onUpdate({ ...section, contentBlocks: newBlocks });

  const changeType = (idx, newExId) => {
    const synth = syntheticBlockForExId(newExId, section, cefrLevel, lessonTitle);
    const newBlocks = blocks.map((b, i) => i === idx ? (synth || { type: EXERCISE_ID_TO_NODE[newExId] || b.type, attrs: {} }) : b);
    updateBlocks(newBlocks);
  };

  const removeExercise = (idx) => updateBlocks(blocks.filter((_, i) => i !== idx));

  const addExercise = () => {
    const defaultId = 'fillinthegaps';
    const synth = syntheticBlockForExId(defaultId, section, cefrLevel, lessonTitle);
    updateBlocks([...blocks, synth || { type: 'fillInTheGapsExercise', attrs: {} }]);
  };

  const submitEdit = () => {
    const txt = editText.trim();
    if (!txt || applying) return;
    onAiEdit(txt);
    setEditText('');
    setEditOpen(false);
  };

  return (
    <div style={{
      borderRadius: 10, border: `1.5px solid ${clr.gray200}`,
      background: '#fff', overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      transition: 'box-shadow 150ms',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '9px 10px 9px 12px',
        background: meta.bg,
        borderBottom: blocks.length || editOpen ? `1px solid ${clr.gray200}` : 'none',
      }}>
        <span style={{ fontSize: 15, flexShrink: 0 }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: clr.gray900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {section.title}
          </div>
          <div style={{ fontSize: 11, color: meta.accent, fontWeight: 600, marginTop: 1 }}>
            {meta.label} · {section.estimatedMinutes} min
          </div>
        </div>

        {/* Edit with AI button */}
        <button
          onClick={() => setEditOpen(v => !v)}
          title="Edit with AI"
          style={{
            width: 26, height: 26, borderRadius: 6,
            border: `1px solid ${editOpen ? meta.accent : clr.gray200}`,
            background: editOpen ? meta.bg : '#fff',
            cursor: 'pointer', color: editOpen ? meta.accent : clr.gray400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, flexShrink: 0, transition: 'all 120ms',
          }}
          onMouseEnter={e => { if (!editOpen) { e.currentTarget.style.borderColor = meta.accent; e.currentTarget.style.color = meta.accent; } }}
          onMouseLeave={e => { if (!editOpen) { e.currentTarget.style.borderColor = clr.gray200; e.currentTarget.style.color = clr.gray400; } }}
        >
          ✏️
        </button>

        {/* Delete button */}
        <button
          onClick={onDelete}
          title="Delete section"
          style={{
            width: 26, height: 26, borderRadius: 6, border: `1px solid ${clr.gray200}`,
            background: '#fff', cursor: 'pointer', color: clr.gray400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, flexShrink: 0, transition: 'all 120ms',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = clr.red; e.currentTarget.style.color = clr.red; e.currentTarget.style.background = clr.redSoft; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = clr.gray200; e.currentTarget.style.color = clr.gray400; e.currentTarget.style.background = '#fff'; }}
        >
          ✕
        </button>
      </div>

      {/* Exercise list */}
      {blocks.length > 0 && (
        <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {blocks.map((block, bi) => (
            <ExerciseRow
              key={bi}
              exId={nodeToExId(block.type)}
              onChange={newExId => changeType(bi, newExId)}
              onRemove={() => removeExercise(bi)}
            />
          ))}
        </div>
      )}

      {/* Add exercise */}
      {blocks.length < 4 && (
        <div style={{ padding: blocks.length > 0 ? '0 10px 8px' : '8px 10px' }}>
          <button
            onClick={addExercise}
            style={{
              width: '100%', padding: '5px 0', borderRadius: 7,
              border: `1px dashed ${clr.gray300}`, background: 'transparent',
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
              color: clr.gray400, transition: 'all 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = meta.accent; e.currentTarget.style.color = meta.accent; e.currentTarget.style.background = meta.bg; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = clr.gray300; e.currentTarget.style.color = clr.gray400; e.currentTarget.style.background = 'transparent'; }}
          >
            + add exercise
          </button>
        </div>
      )}

      {/* AI edit field */}
      {editOpen && (
        <div style={{ padding: '8px 10px', borderTop: `1px solid ${clr.gray200}`, background: clr.gray50, display: 'flex', gap: 5 }}>
          <input
            autoFocus
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitEdit()}
            placeholder="e.g. Make the text shorter, focus on AI…"
            disabled={applying}
            style={{
              flex: 1, padding: '6px 9px', borderRadius: 7, fontSize: 12,
              border: `1.5px solid ${clr.gray200}`, outline: 'none',
              background: '#fff', color: clr.gray900,
            }}
            onFocus={e => e.currentTarget.style.borderColor = meta.accent}
            onBlur={e => e.currentTarget.style.borderColor = clr.gray200}
          />
          <button
            onClick={submitEdit}
            disabled={!editText.trim() || applying}
            style={{
              padding: '6px 12px', borderRadius: 7, border: 'none',
              background: editText.trim() && !applying ? meta.accent : clr.gray200,
              color: editText.trim() && !applying ? '#fff' : clr.gray400,
              cursor: editText.trim() && !applying ? 'pointer' : 'not-allowed',
              fontSize: 12, fontWeight: 700, flexShrink: 0, transition: 'all 120ms',
            }}
          >
            {applying ? '…' : 'Apply'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── OutlinePreview ───────────────────────────────────────────────────────────

export default function OutlinePreview({
  outline,
  sections,
  onSectionsChange,
  onInsert,
  onRegenerate,
  onModifySection,
  onModifyAll,
  modifyingAll,
  modifyingSectionIdx,
  generating,
}) {
  const [modifyText, setModifyText] = useState('');

  const updateSection = (idx, updated) => {
    onSectionsChange(sections.map((s, i) => i === idx ? updated : s));
  };

  const deleteSection = (idx) => {
    if (sections.length <= 1) return;
    onSectionsChange(sections.filter((_, i) => i !== idx));
  };

  const handleModifyAll = () => {
    const txt = modifyText.trim();
    if (!txt || modifyingAll) return;
    onModifyAll(txt).then(() => setModifyText(''));
  };

  const cefrLevel = outline?.cefrLevel || 'B1';
  const lessonTitle = outline?.lessonTitle || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <style>{`
        @keyframes opPreviewFade { from { opacity:0; transform:translateY(3px); } to { opacity:1; transform:translateY(0); } }
        .op-section-card { animation: opPreviewFade 0.18s ease both; }
      `}</style>

      {/* Lesson meta */}
      <div style={{ padding: '0 0 10px', borderBottom: `1px solid ${clr.gray200}`, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: clr.gray900, marginBottom: 2, lineHeight: 1.35 }}>
          {outline?.lessonTitle || 'Untitled lesson'}
        </div>
        <div style={{ fontSize: 11, color: clr.gray400 }}>
          {cefrLevel} · {outline?.requestedDurationMinutes} min · {sections.length} section{sections.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Section cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sections.map((sec, i) => (
          <div key={sec.id || i} className="op-section-card" style={{ animationDelay: `${i * 30}ms` }}>
            <SectionCard
              section={sec}
              cefrLevel={cefrLevel}
              lessonTitle={lessonTitle}
              onUpdate={updated => updateSection(i, updated)}
              onDelete={() => deleteSection(i)}
              onAiEdit={instruction => onModifySection(i, instruction)}
              applying={modifyingSectionIdx === i}
            />
          </div>
        ))}
      </div>

      {/* Global modify */}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: clr.gray400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          What to change?
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <input
            value={modifyText}
            onChange={e => setModifyText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleModifyAll()}
            placeholder="e.g. Remove grammar, add a dialogue activity"
            disabled={modifyingAll}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 12,
              border: `1.5px solid ${clr.gray200}`, outline: 'none',
              background: '#fff', color: clr.gray900, transition: 'border-color 150ms',
            }}
            onFocus={e => e.currentTarget.style.borderColor = clr.purple}
            onBlur={e => e.currentTarget.style.borderColor = clr.gray200}
          />
          <button
            onClick={handleModifyAll}
            disabled={!modifyText.trim() || modifyingAll}
            style={{
              padding: '8px 12px', borderRadius: 8, border: 'none', flexShrink: 0,
              background: modifyText.trim() && !modifyingAll
                ? 'linear-gradient(135deg, #7c3aed, #a855f7)'
                : clr.gray200,
              color: modifyText.trim() && !modifyingAll ? '#fff' : clr.gray400,
              cursor: modifyText.trim() && !modifyingAll ? 'pointer' : 'not-allowed',
              fontSize: 12, fontWeight: 700, transition: 'all 150ms',
            }}
          >
            {modifyingAll ? '…' : 'Apply'}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <button
          onClick={onInsert}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 9, border: 'none',
            background: clr.gray900, color: '#fff',
            cursor: 'pointer', fontWeight: 700, fontSize: 13,
            transition: 'background 150ms',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1f2937'}
          onMouseLeave={e => e.currentTarget.style.background = clr.gray900}
        >
          Insert into lesson →
        </button>
        <button
          onClick={onRegenerate}
          disabled={generating}
          title="Regenerate from scratch"
          style={{
            padding: '10px 14px', borderRadius: 9,
            border: `1.5px solid ${clr.gray200}`, background: '#fff',
            cursor: generating ? 'wait' : 'pointer',
            fontSize: 16, color: clr.gray600, transition: 'border-color 150ms',
          }}
          onMouseEnter={e => { if (!generating) e.currentTarget.style.borderColor = clr.purple; }}
          onMouseLeave={e => e.currentTarget.style.borderColor = clr.gray200}
        >
          ↺
        </button>
      </div>
    </div>
  );
}
